import { Injectable } from '@angular/core';
import { OmnitureData, OmnitureLinkType } from './../models';

interface Window {
  s: any;
}

declare var window: Window;

@Injectable()
export class OmnitureService {

  /**
   * Tracks a page view with the given data and sends it to Adobe Omniture
   * @param { OmnitureData } data Object that contains a list of evars/props/events for omniture
   * @see https://marketing.adobe.com/resources/help/en_US/sc/implement/function_t.html
   */
  pageEvent(data: OmnitureData) {
    if (window && window.s) {
      window.s.t(data);
    }
  }

  /**
   * Processes a click event to the Adobe Omniture javascript
   * Used to pass data that you *DO NOT* want to be a page view
   * @param { string } event - Event to pass to omniture
   * @param { Element } element - The element responsible for the click event
   * @param { OmnitureLinkType } linkType - Type of link we are tracking -- custom, download, or exit
   * @param { OmnitureData } eventData - evar/sprop data to be overwritten, can be null
   * @see https://marketing.adobe.com/resources/help/en_US/sc/implement/function_tl.html
   */
  clickEvent(event: string, element: Element|boolean, linkType?: OmnitureLinkType, data?: OmnitureData) {
    // Initialize our link type and element reference
    if (!linkType) linkType = OmnitureLinkType.CUSTOM_LINK;
    if (!element) element = true;

    // Ensure we are in a browser and have omniture loaded
    if (window && window.s) {

      // Handle data elements if needed, and fire tracking
      if (data) this.setupWindowData(data);
      window.s.tl(element, linkType, event, data);
      if (data) this.clearWindowData();
    }
  }

  /**
   * Passes in custom data we care about for a given s.tl call
   * @param { OmnitureData } data - the data we need to add to the window and allow omniture to use
   */
  private setupWindowData(data: OmnitureData) {

    // First clear out the window data to make sure we aren't duplicating events from load
    this.clearWindowData();

    // For every element in the data, setup the window.s values to allow certain evar/sprops to be added
    // https://marketing.adobe.com/resources/help/en_US/sc/implement/linkTrackVars.html
    Object.keys(data).map((key) => {
      window.s.linkTrackVars = window.s.linkTrackVars ? `${window.s.linkTrackVars},${key}` : `${key}`;
      window.s[key] = data[key];

      // If we are dealing with events, populate the linkTrackEvents variable
      // https://marketing.adobe.com/resources/help/en_US/sc/implement/link_track_vars_events.html
      if (key === 'events') {
        window.s.linkTrackEvents = data[key];
      }
    });

  }

  /**
   * Clear out variables from omniture object
   */
  private clearWindowData() {
    window.s.clearVars();
    window.s.linkTrackVars = null;
    window.s.linkTrackEvents = null;
  }

}
